import { useQueries, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';

import { listMyClasses } from '@/api/classes';
import { getJob, listJobs } from '@/api/jobs';
import type { JobDetailResponse } from '@/api/jobs';
import { getReviewQueueAll } from '@/api/review';
import { Button } from '@/components/ui/button/button';
import { useAuthStore } from '@/stores/authStore';
import type { JobOut } from '@/types';

import styles from './teacher-dashboard.module.css';

/* ===== 小型线性图标 ===== */
function Icon({ name }: { name: string }) {
  const common = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
    case 'doc':
      return (
        <svg {...common}>
          <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V8z" />
          <path d="M14 3v5h5" />
        </svg>
      );
    case 'school':
      return (
        <svg {...common}>
          <path d="M22 10 12 5 2 10l10 5 10-5z" />
          <path d="M6 12.5V17a6 6 0 0 0 12 0v-4.5" />
        </svg>
      );
    case 'robot':
      return (
        <svg {...common}>
          <rect x="4" y="8" width="16" height="10" rx="3" />
          <path d="M12 8V5M9 3.5h6" />
          <circle cx="9" cy="12.5" r="1" />
          <circle cx="15" cy="12.5" r="1" />
          <path d="M9.5 15.5c1 .8 4 .8 5 0" />
        </svg>
      );
    case 'pen':
      return (
        <svg {...common}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
        </svg>
      );
    case 'sync':
      return (
        <svg {...common}>
          <path d="M21 12a9 9 0 0 1-9 9 9.2 9.2 0 0 1-6.4-2.6" />
          <path d="M3 12a9 9 0 0 1 9-9 9.2 9.2 0 0 1 6.4 2.6" />
          <path d="M21 5v7h-7M3 19v-7h7" />
        </svg>
      );
    case 'folder':
      return (
        <svg {...common}>
          <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        </svg>
      );
    case 'team':
      return (
        <svg {...common}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'flow':
      return (
        <svg {...common}>
          <circle cx="5" cy="6" r="2.2" />
          <circle cx="5" cy="18" r="2.2" />
          <circle cx="19" cy="12" r="2.2" />
          <path d="M5 8.2v7.6M7.1 6.5h7a2.5 2.5 0 0 1 2.5 2.5v.8M7.1 17.5h7a2.5 2.5 0 0 0 2.5-2.5v-.8" />
        </svg>
      );
    case 'chart':
      return (
        <svg {...common}>
          <path d="M3 3v18h18" />
          <path d="M7 15v-4M12 15V7M17 15v-7" />
        </svg>
      );
    case 'calendar':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M8 3v4M16 3v4M3 10h18" />
        </svg>
      );
    case 'chevron':
      return (
        <svg {...common}>
          <path d="M9 18l6-6-6-6" />
        </svg>
      );
    default:
      return null;
  }
}

function formatDateTime(iso?: string | null, fallback = '未设置') {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}月${d.getDate()}日 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 作业状态语义 */
function jobStatus(job: JobOut, detail?: JobDetailResponse): { label: string; tone: 'green' | 'orange' | 'gray' } {
  if (job.status === 'closed') return { label: '已归档', tone: 'gray' };
  const subs = detail?.submissions ?? [];
  const hasPending = subs.some((s) => s.status === 'submitted' && !s.review_status);
  if (hasPending) return { label: '待批阅', tone: 'orange' };
  if (job.status === 'published') return { label: '已发布', tone: 'green' };
  return { label: '草稿', tone: 'gray' };
}

const quickLinks = [
  { to: '/jobs', icon: 'doc', title: '作业管理', desc: '布置 / 发布 / 草稿' },
  { to: '/classroom', icon: 'school', title: '班级管理', desc: '班级 / 成员 / 邀请' },
  { to: '/review/queue', icon: 'flow', title: '复核工作流', desc: 'AI 初评 → 教师确认' },
  { to: '/class/analytics', icon: 'chart', title: '班级分析', desc: '成绩 / 薄弱知识点' },
];

export function TeacherDashboard() {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();

  const { data: jobsData, isLoading } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => listJobs(),
  });

  const { data: myClasses } = useQuery({
    queryKey: ['my-classes'],
    queryFn: () => listMyClasses(),
  });

  const { data: queue } = useQuery({
    queryKey: ['reviewQueueAll'],
    queryFn: () => getReviewQueueAll(),
  });

  const recentJobs = (jobsData?.items ?? []).slice(0, 5);

  // 并发拉取最近作业详情，用于「已提交 / 总人数」进度
  const detailQueries = useQueries({
    queries: recentJobs.map((job) => ({
      queryKey: ['job', job.id],
      queryFn: () => getJob(job.id),
    })),
  });
  const detailMap = new Map<number, JobDetailResponse>();
  recentJobs.forEach((job, i) => {
    if (detailQueries[i]?.data) detailMap.set(job.id, detailQueries[i].data!);
  });

  const classMap = new Map((myClasses ?? []).map((c) => [c.id, c]));
  const totalStudents = (myClasses ?? []).reduce((sum, c) => sum + (c.member_count ?? 0), 0);

  const jobTotal = jobsData?.total ?? 0;
  const progress = queue?.progress;
  const pendingCount = progress?.pending ?? 0;
  const reviewedCount = progress?.reviewed ?? 0;
  const totalSubmissions = progress?.total ?? 0;
  const pendingReviewCount = Math.max(0, totalSubmissions - pendingCount - reviewedCount);
  const aiAssistedCount = reviewedCount + pendingCount;
  const aiPercent = totalSubmissions > 0 ? Math.round((aiAssistedCount / totalSubmissions) * 100) : 0;

  const stats = [
    {
      icon: 'doc',
      value: jobTotal,
      suffix: '份',
      label: '我创建的作业',
      hint: '本学期累计布置',
      tone: 'blue',
      to: '/jobs',
    },
    {
      icon: 'school',
      value: myClasses?.length ?? 0,
      suffix: '个',
      label: '我管理的班级',
      hint: `覆盖 ${totalStudents} 名学生`,
      tone: 'purple',
      to: '/classroom',
    },
    {
      icon: 'robot',
      value: aiPercent,
      suffix: '%',
      label: 'AI 智能初评辅助',
      hint: `已自动初评 ${aiAssistedCount} 份`,
      tone: 'green',
      to: '/review/queue',
    },
    {
      icon: 'pen',
      value: pendingCount,
      suffix: '份',
      label: '待批阅作业',
      hint: '需教师确认',
      tone: 'orange',
      to: '/review/queue',
    },
    {
      icon: 'sync',
      value: pendingReviewCount,
      suffix: '份',
      label: '待复核作业',
      hint: '已批待复核',
      tone: 'gray',
      to: '/review/queue',
    },
  ];

  return (
    <div className={styles.page}>
      {/* 页面头部 */}
      <header className={styles.header}>
        <div>
          <h1 className={styles.greeting}>欢迎回来，{user?.display_name ?? '老师'}</h1>
          <p className={styles.subtitle}>教师工作台 · 作业与复核概览</p>
        </div>
        <Button onClick={() => navigate('/jobs/create')}>+ 新建作业</Button>
      </header>

      {/* 统计卡片 */}
      <div className={styles.statsRow}>
        {stats.map((s) => (
          <Link
            key={s.label}
            to={s.to}
            className={`card card-hover ${styles.statCard}`}
          >
            <div className={`${styles.statIcon} ${styles[s.tone]}`}>
              <Icon name={s.icon} />
            </div>
            <div className={styles.statBody}>
              <div className={styles.statValue}>
                {s.value}
                <span className={styles.statSuffix}>{s.suffix}</span>
              </div>
              <div className={styles.statLabel}>{s.label}</div>
              <div className={styles.statHint}>{s.hint}</div>
            </div>
          </Link>
        ))}
      </div>

      {/* 主体分栏 */}
      <div className={styles.mainRow}>
        {/* 快捷入口 */}
        <section className={`card ${styles.quickPanel}`}>
          <h2 className={styles.panelTitle}>快捷入口</h2>
          <div className={styles.quickList}>
            {quickLinks.map((q) => (
              <button
                key={q.to}
                type="button"
                className={styles.quickItem}
                onClick={() => navigate(q.to)}
              >
                <span className={styles.quickIcon}>{<Icon name={q.icon} />}</span>
                <span className={styles.quickTextWrap}>
                  <span className={styles.quickTitle}>{q.title}</span>
                  <span className={styles.quickDesc}>{q.desc}</span>
                </span>
                <span className={styles.quickArrow}>
                  <Icon name="chevron" />
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* 最近作业 */}
        <section className={`card ${styles.jobsPanel}`}>
          <div className={styles.panelHead}>
            <h2 className={styles.panelTitle}>最近作业</h2>
            <button type="button" className={styles.moreLink} onClick={() => navigate('/jobs')}>
              查看全部
              <Icon name="chevron" />
            </button>
          </div>

          {isLoading && (
            <div className={styles.emptyState}>
              <span className={styles.loadingDot} />
              正在加载作业…
            </div>
          )}

          {!isLoading && jobTotal === 0 && (
            <div className={styles.emptyState}>
              <span className={styles.emptyIcon}>
                <Icon name="doc" />
              </span>
              <p className={styles.emptyText}>暂无作业</p>
              <p className={styles.emptyHint}>点击右上角「新建作业」开始创建</p>
            </div>
          )}

          {!isLoading && jobTotal > 0 && (
            <div className={styles.jobList}>
              {recentJobs.map((job) => {
                const detail = detailMap.get(job.id);
                const status = jobStatus(job, detail);
                const cls = classMap.get(job.class_id ?? -1);
                const submitted = detail?.submitted_count ?? 0;
                const total = detail?.class_member_count ?? 0;
                const ratio = total > 0 ? Math.min(100, Math.round((submitted / total) * 100)) : 0;
                return (
                  <div key={job.id} className={styles.jobItem}>
                    <div className={styles.jobMain}>
                      <div className={styles.jobTitleRow}>
                        <span className={styles.jobTitle}>{job.title}</span>
                        <span className={`tag tag-${status.tone}`}>{status.label}</span>
                      </div>
                      <div className={styles.jobMeta}>
                        <span className={styles.jobMetaItem}>
                          <Icon name="school" />
                          {cls?.name ?? '未关联班级'}
                        </span>
                        <span className={styles.jobMetaItem}>
                          <Icon name="calendar" />
                          布置 {formatDateTime(job.created_at)}
                        </span>
                        <span className={styles.jobMetaItem}>
                          <Icon name="calendar" />
                          截止 {formatDateTime(job.due_date, '未设置')}
                        </span>
                      </div>
                    </div>

                    <div className={styles.jobProgress}>
                      <div className={styles.progressLabel}>
                        <span>{total > 0 ? `${submitted}/${total}` : `${submitted} 已交`}</span>
                        <span>{ratio}%</span>
                      </div>
                      <div className={styles.progressTrack}>
                        <div className={styles.progressFill} style={{ width: `${ratio}%` }} />
                      </div>
                    </div>

                    <div className={styles.jobAction}>
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => navigate(`/jobs/${job.id}`)}
                      >
                        查看详情
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* 迷你数据概览 */}
      <section className={`card ${styles.overview}`}>
        <div className={styles.panelHead}>
          <div>
            <h2 className={styles.panelTitle}>作业提交完成概览</h2>
            <p className={styles.panelHint}>最近作业的提交完成情况</p>
          </div>
        </div>
        {recentJobs.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyHint}>创建并发布作业后，这里会展示提交完成率</p>
          </div>
        ) : (
          <div className={styles.barGrid}>
            {recentJobs.map((job) => {
              const detail = detailMap.get(job.id);
              const submitted = detail?.submitted_count ?? 0;
              const total = detail?.class_member_count ?? 0;
              const ratio = total > 0 ? Math.min(100, Math.round((submitted / total) * 100)) : 0;
              return (
                <div key={job.id} className={styles.barCard}>
                  <span className={styles.barName} title={job.title}>
                    {job.title}
                  </span>
                  <div className={styles.barTrack}>
                    <div className={styles.barFill} style={{ width: `${ratio}%` }} />
                  </div>
                  <span className={styles.barValue}>
                    {total > 0 ? `${submitted}/${total}` : `${submitted} 已交`}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
